import { create } from 'zustand';
import apiClient from '../lib/api';

/** Effective per-resource rights the backend resolves for the signed-in user. */
export interface VaultFlags {
  view: boolean;
  reveal: boolean;
  create?: boolean;
  edit: boolean;
  share: boolean;
  delete: boolean;
}

export interface Vault {
  id: string;
  vaultType: 'personal' | 'team';
  name: string;
  description?: string | null;
  ownerUserId: string;
  status: 'active' | 'archived';
  itemCount?: number;
  permissions?: VaultFlags;
  createdAt: string;
  updatedAt: string;
}

export interface VaultItem {
  id: string;
  vaultId: string;
  title: string;
  itemType: 'website_login' | 'database' | 'api_key' | 'ssh_key' | 'secure_note' | 'other';
  template?: string | null;
  project?: string | null;
  username?: string | null;
  websiteUrl?: string | null;
  environment?: string | null;
  tags: string[];
  status: 'active' | 'archived' | 'deleted';
  versionNumber: number;
  nextRotationAt?: string | null;
  lastRotatedAt?: string | null;
  permissions?: VaultFlags;
  updatedAt: string;
}

export interface VaultMember {
  id: string;
  vaultId: string;
  userId: string;
  role: 'owner' | 'manager' | 'member' | 'viewer';
  canView: boolean; canReveal: boolean; canCreate: boolean;
  canEdit: boolean; canShare: boolean; canDelete: boolean;
  expiresAt?: string | null;
  revokedAt?: string | null;
}

export interface AccessRequest {
  id: string;
  vaultId: string;
  itemId?: string | null;
  requesterId: string;
  requestedPermissions: string[];
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired';
  decidedBy?: string | null;
  requestedAt: string;
  expiresAt?: string | null;
}

export interface RotationJob {
  id: string;
  itemId: string;
  status: 'scheduled' | 'in_progress' | 'succeeded' | 'failed' | 'cancelled';
  method: string;
  externalChanged: boolean;
  note?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface VaultAuditEvent {
  id: string;
  vaultId?: string | null;
  itemId?: string | null;
  actorUserId?: string | null;
  action: string;
  outcome: string;
  reasonCode?: string | null;
  occurredAt: string;
  metadata: Record<string, unknown>;
}

export interface VaultDashboard {
  personalVaults: number;
  teamVaults: number;
  items: number;
  pendingApprovals: number;
  upcomingRotations: number;
}

const unwrap = (res: any) => res.data?.data ?? res.data;

interface VaultState {
  vaults: Vault[];
  isLoading: boolean;
  error: string | null;

  fetchDashboard: () => Promise<VaultDashboard | null>;
  fetchVaults: (type?: 'personal' | 'team') => Promise<void>;
  getVault: (id: string) => Promise<Vault>;
  createVault: (data: { vaultType: string; name?: string; description?: string }) => Promise<Vault>;
  updateVault: (id: string, data: Partial<Vault>) => Promise<Vault>;

  listItems: (vaultId: string, params?: any) => Promise<VaultItem[]>;
  listProjects: (vaultId: string) => Promise<string[]>;
  createItem: (vaultId: string, data: any) => Promise<VaultItem>;
  updateItem: (itemId: string, data: any) => Promise<VaultItem>;
  revealItem: (itemId: string, reason?: string) => Promise<{ id: string; title: string; itemType: string; secret: Record<string, any> }>;
  archiveItem: (itemId: string) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;

  listMembers: (vaultId: string) => Promise<VaultMember[]>;
  addMember: (vaultId: string, data: any) => Promise<VaultMember>;
  updateMember: (vaultId: string, memberId: string, data: any) => Promise<VaultMember>;
  removeMember: (vaultId: string, memberId: string) => Promise<void>;

  listRequests: (status?: string) => Promise<AccessRequest[]>;
  createRequest: (data: any) => Promise<AccessRequest>;
  decideRequest: (id: string, decision: 'approve' | 'reject' | 'cancel', decisionReason?: string) => Promise<AccessRequest>;

  listRotationJobs: (status?: string) => Promise<RotationJob[]>;
  scheduleRotation: (itemId: string, data: any) => Promise<RotationJob>;
  completeRotation: (jobId: string, data: any) => Promise<RotationJob>;

  listAudit: (params?: any) => Promise<VaultAuditEvent[]>;
  generatePassword: (policy?: any) => Promise<string>;
}

export const useVaultStore = create<VaultState>((set) => ({
  vaults: [],
  isLoading: false,
  error: null,

  fetchDashboard: async () => {
    try {
      const res = await apiClient.get('/vault/dashboard');
      return unwrap(res) as VaultDashboard;
    } catch {
      return null;
    }
  },

  fetchVaults: async (type) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/vault/vaults', { params: type ? { type } : {} });
      set({ vaults: unwrap(res), isLoading: false });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Failed to load vaults', isLoading: false });
    }
  },

  getVault: async (id) => unwrap(await apiClient.get(`/vault/vaults/${id}`)),

  createVault: async (data) => {
    const v = unwrap(await apiClient.post('/vault/vaults', data));
    set((s) => ({ vaults: [v, ...s.vaults] }));
    return v;
  },

  updateVault: async (id, data) => {
    const v = unwrap(await apiClient.patch(`/vault/vaults/${id}`, data));
    set((s) => ({ vaults: s.vaults.map((x) => (x.id === id ? v : x)) }));
    return v;
  },

  listItems: async (vaultId, params) => unwrap(await apiClient.get(`/vault/vaults/${vaultId}/items`, { params })),
  listProjects: async (vaultId) => unwrap(await apiClient.get(`/vault/vaults/${vaultId}/projects`)),
  createItem: async (vaultId, data) => unwrap(await apiClient.post(`/vault/vaults/${vaultId}/items`, data)),
  updateItem: async (itemId, data) => unwrap(await apiClient.patch(`/vault/items/${itemId}`, data)),
  revealItem: async (itemId, reason) => unwrap(await apiClient.post(`/vault/items/${itemId}/reveal`, { reason })),
  archiveItem: async (itemId) => { await apiClient.post(`/vault/items/${itemId}/archive`); },
  deleteItem: async (itemId) => { await apiClient.delete(`/vault/items/${itemId}`); },

  listMembers: async (vaultId) => unwrap(await apiClient.get(`/vault/vaults/${vaultId}/members`)),
  addMember: async (vaultId, data) => unwrap(await apiClient.post(`/vault/vaults/${vaultId}/members`, data)),
  updateMember: async (vaultId, memberId, data) => unwrap(await apiClient.patch(`/vault/vaults/${vaultId}/members/${memberId}`, data)),
  removeMember: async (vaultId, memberId) => { await apiClient.delete(`/vault/vaults/${vaultId}/members/${memberId}`); },

  listRequests: async (status) => unwrap(await apiClient.get('/vault/access-requests', { params: status ? { status } : {} })),
  createRequest: async (data) => unwrap(await apiClient.post('/vault/access-requests', data)),
  decideRequest: async (id, decision, decisionReason) =>
    unwrap(await apiClient.post(`/vault/access-requests/${id}/${decision}`, { decisionReason })),

  listRotationJobs: async (status) => unwrap(await apiClient.get('/vault/rotation-jobs', { params: status ? { status } : {} })),
  scheduleRotation: async (itemId, data) => unwrap(await apiClient.post(`/vault/items/${itemId}/rotation-jobs`, data)),
  completeRotation: async (jobId, data) => unwrap(await apiClient.patch(`/vault/rotation-jobs/${jobId}`, data)),

  listAudit: async (params) => unwrap(await apiClient.get('/vault/audit-events', { params })),
  generatePassword: async (policy) => {
    const res = await apiClient.post('/vault/password', policy || {});
    return (unwrap(res) as { password: string }).password;
  },
}));
